'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Wifi, WifiOff, Database, AlertCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { API_CONFIG } from '@/lib/api/config';

interface HealthResponse {
  status: string;
  database: 'connected' | 'disconnected';
  timestamp: string;
}

type ConnectionStatus = 'connected' | 'disconnected' | 'checking';

const POLL_INTERVAL = 30_000; // 30 seconds

export function ConnectivityStatusFooter() {
  const t = useTranslations('handoverScanning');
  const [systemStatus, setSystemStatus] =
    useState<ConnectionStatus>('checking');
  const [dbStatus, setDbStatus] = useState<ConnectionStatus>('checking');

  useEffect(() => {
    let mounted = true;

    async function checkHealth() {
      try {
        const res = await fetch(`${API_CONFIG.baseURL}/api/health`, {
          signal: AbortSignal.timeout(5000)
        });

        if (!mounted) return;

        if (res.ok) {
          const data: HealthResponse = await res.json();
          setSystemStatus('connected');
          setDbStatus(
            data.database === 'connected' ? 'connected' : 'disconnected'
          );
        } else {
          setSystemStatus('disconnected');
          setDbStatus('disconnected');
        }
      } catch {
        if (!mounted) return;
        setSystemStatus('disconnected');
        setDbStatus('disconnected');
      }
    }

    checkHealth();
    const interval = setInterval(checkHealth, POLL_INTERVAL);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <div className='bg-muted/50 border-t px-4 py-2'>
      <div className='flex items-center gap-6'>
        {/* System status */}
        <StatusIndicator
          status={systemStatus}
          label={
            systemStatus === 'connected'
              ? t('systemConnected')
              : systemStatus === 'disconnected'
                ? t('systemDisconnected')
                : t('systemChecking')
          }
          icon={systemStatus === 'disconnected' ? WifiOff : Wifi}
        />

        {/* Database status */}
        <StatusIndicator
          status={dbStatus}
          label={
            dbStatus === 'connected'
              ? t('databaseSynced')
              : dbStatus === 'disconnected'
                ? t('databaseDisconnected')
                : t('systemChecking')
          }
          icon={dbStatus === 'disconnected' ? AlertCircle : Database}
        />
      </div>
    </div>
  );
}

function StatusIndicator({
  status,
  label,
  icon: Icon
}: {
  status: ConnectionStatus;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className='flex items-center gap-2'>
      <span
        className={cn(
          'h-2 w-2 rounded-full',
          status === 'connected' && 'bg-green-500',
          status === 'disconnected' && 'bg-red-500',
          status === 'checking' && 'animate-pulse bg-yellow-500'
        )}
      />
      <Icon
        className={cn(
          'h-3.5 w-3.5',
          status === 'connected' && 'text-green-600 dark:text-green-400',
          status === 'disconnected' && 'text-red-600 dark:text-red-400',
          status === 'checking' && 'text-yellow-600 dark:text-yellow-400'
        )}
      />
      <span
        className={cn(
          'text-xs font-medium',
          status === 'connected' && 'text-green-600 dark:text-green-400',
          status === 'disconnected' && 'text-red-600 dark:text-red-400',
          status === 'checking' && 'text-yellow-600 dark:text-yellow-400'
        )}
      >
        {label}
      </span>
    </div>
  );
}
